/**
 * computeBindWeights — Hand Studio E4 / ADR-010 §2 ④ "Bind" (geodesic voxel bind,
 * hand region only). ONE responsibility: decide, for every hand-region vertex, HOW
 * MUCH each grafted digit bone should move it. Pure data in, pure data out — voxels,
 * positions, bone ids. No mesh, no engine, no `three` (ADR-002 §6): the caller seats
 * the result into whatever skinning representation it has, and the weights stay
 * testable without a scene.
 *
 * WHY GEODESIC AND NOT EUCLIDEAN. This is the whole claim ADR-010 makes: the bind
 * "inherently prevents inter-digit weight bleed while flowing smoothly through fused
 * volume". A fingertip and its neighbour's fingertip are Euclidean-ADJACENT — a
 * nearest-bone-in-space rule bleeds the index bone into pinky vertices and the hand
 * curls as a slab. Geodesically they are FAR: the only path through occupied volume
 * runs down to the webbing and back up, tens of voxels. So distance is measured by
 * 6-connected BFS THROUGH THE VOLUME, and the separation the analyzer found is the
 * same separation the weights inherit. Where the volume genuinely is fused — a
 * webbed frog hand, a mitten — the geodesic distance is genuinely short and the
 * weights genuinely blend. The metric does the anatomy; nothing here special-cases
 * webbing.
 *
 * WHY TRILINEAR AND NOT NEAREST-VOXEL. The Voxel-Skinner precedent, recorded before
 * this was written: per-voxel nearest-snap sampling makes dense meshes deform in
 * visible BOXES, because every vertex inside one voxel gets bit-identical weights
 * and the discontinuity at the voxel wall becomes a facet you can see. So the voxel
 * field is the CARRIER, not the answer: each vertex trilinearly samples the eight
 * voxels around it ({@link sampleTrilinear}), which makes the weight field C⁰ across
 * voxel walls and the deformation smooth at any mesh density. Retro-fitting this
 * means rewriting the sampler, so it is here from the first commit.
 *
 * THE RETAIN COMPETITORS ARE LOAD-BEARING. Segments flagged `retain` (the rig's own
 * hand and forearm) compete for weight but are never emitted. That is what makes the
 * output a SHARE rather than a partition: palm vertices sit close to the retained
 * hand bone, so the digit weights there sum to ~0 and the caller leaves those
 * vertices alone; fingertip vertices sum to ~1 and the caller hands them over
 * wholesale. Without a retain competitor, inverse-distance would normalise across
 * digit bones ONLY and every palm vertex would be dragged by whichever knuckle
 * happened to be nearest. {@link BindWeightsResult.influences} therefore does NOT
 * sum to 1 per vertex, by design — see `digitShare`.
 *
 * Fail-loud (LAWS, ADR-010 §7): a grid with no world embedding, an empty segment
 * list, or a non-finite option throws {@link BindWeightsError} — those are wiring
 * bugs. A bone whose segment lies OUTSIDE the volume (a tip extrapolated past the
 * mesh) and a vertex that lands in no occupied voxel are honest product outcomes of
 * a real specimen, so they are named EvidenceMarkers with counts, never silence: an
 * unseeded bone moves nothing and the session must be able to see that it does.
 *
 * Depends on: hands/voxel-grid, hands/separation-profile (EvidenceMarker), math/vec3.
 * Depended on by: kernel index, the adapter-three bind seam, run-document.
 */
import { KernelError } from '../ir/errors.js';
import type { Vec3Data } from '../math/vec3.js';
import {
  voxelCoords,
  voxelIndex,
  type VoxelGrid,
  type VoxelPoint,
} from './voxel-grid.js';
import type { EvidenceMarker } from './separation-profile.js';

/** A cannot-bind condition — a producer/caller bug, fail-loud (ADR-010 §7). */
export class BindWeightsError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot compute bind weights (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

/**
 * One bone's influence region, as the line segment it occupies in VOXEL-INDEX space
 * (the space E3a places joints in). `retain: true` marks an EXISTING bone that
 * competes for weight but is never emitted — see the module header.
 */
export interface BoneSegment {
  readonly boneId: string;
  readonly from: VoxelPoint;
  readonly to: VoxelPoint;
  readonly retain?: boolean;
}

/** A hand-region vertex: its index in the caller's geometry, and its BIND-WORLD position. */
export interface BindVertex {
  readonly index: number;
  readonly position: Vec3Data;
}

/** One computed influence. Weights are an absolute SHARE, not a partition — see the header. */
export interface VertexInfluence {
  readonly vertexIndex: number;
  readonly boneId: string;
  readonly weight: number;
}

export interface BindWeightsOptions {
  /**
   * Inverse-distance exponent p in `w ∝ d⁻ᵖ` (default 3). Higher = tighter, more
   * bone-local weights and a sharper knuckle; lower = softer blend across joints.
   * At p=3 a vertex twice as far geodesically gets an eighth the pull, which is
   * what collapses cross-digit influence to noise before the floor even runs.
   */
  readonly falloff?: number;
  /** Max emitted influences per vertex (default 4 — three.js skins four). */
  readonly maxInfluences?: number;
  /**
   * Drop any bone whose weight is below this FRACTION of the strongest bone at that
   * vertex (default 1e-3), before emission. Inverse distance has infinite support,
   * so without a floor the index bone holds a 1e-9 stake in every pinky vertex and
   * "no bleed" can only ever be tested as an epsilon. With it, no-bleed is EXACTLY
   * zero and the test says what it means.
   */
  readonly bleedFloor?: number;
  /** Occupied voxels within this radius of a segment seed it (default 1.0 voxel). */
  readonly seedRadiusVoxels?: number;
}

export interface BindWeightsResult {
  /** Grafted-bone influences only, ascending by vertex then descending by weight. */
  readonly influences: readonly VertexInfluence[];
  /** Vertices that received at least one influence. */
  readonly verticesBound: number;
  /** Vertices whose trilinear stencil found no occupied voxel — left untouched. */
  readonly verticesOutsideVolume: number;
  /** Largest per-vertex digit share seen; ~1 somewhere means a fingertip is fully driven. */
  readonly maxDigitShare: number;
  readonly markers: readonly EvidenceMarker[];
}

const sub = (a: VoxelPoint, b: VoxelPoint): VoxelPoint => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const dot = (a: VoxelPoint, b: VoxelPoint): number => a.x * b.x + a.y * b.y + a.z * b.z;

/** Squared distance from a point to a finite segment (voxel-index space). */
function distSqToSegment(p: VoxelPoint, a: VoxelPoint, b: VoxelPoint): number {
  const ab = sub(b, a);
  const denom = dot(ab, ab);
  const t = denom > 0 ? Math.max(0, Math.min(1, dot(sub(p, a), ab) / denom)) : 0;
  const dx = p.x - (a.x + ab.x * t);
  const dy = p.y - (a.y + ab.y * t);
  const dz = p.z - (a.z + ab.z * t);
  return dx * dx + dy * dy + dz * dz;
}

/**
 * The 6-connected geodesic distance field from a seed set, in voxels, restricted to
 * occupied volume. Unit edge cost: BFS is exact for the 6-connected metric, so no
 * priority queue is needed and repeat runs are bit-identical (ADR-005 §9).
 * Unreached voxels stay `Infinity` — which is what gives a distant digit exactly no
 * pull rather than a small one.
 *
 * Exported for the SECOND volume consumer (ADR-021): weights/support-field
 * measures clavicle support through the same metric, so the separation the
 * hands inherit and the bound the chest repair enforces are one geometry.
 * `slot` maps linear voxel index → dense position in `occupiedList` (-1 =
 * unoccupied); the returned distances are indexed by that dense slot.
 */
export function geodesicField(
  seeds: readonly number[],
  dims: readonly [number, number, number],
  slot: Int32Array,
  occupiedList: readonly number[],
): Float32Array {
  const dist = new Float32Array(occupiedList.length).fill(Infinity);
  const queue = new Int32Array(occupiedList.length);
  let head = 0;
  let tail = 0;
  for (const s of seeds) {
    const si = slot[s]!;
    if (si >= 0 && dist[si] === Infinity) {
      dist[si] = 0;
      queue[tail] = s;
      tail += 1;
    }
  }
  const [nx, ny] = dims;
  while (head < tail) {
    const cur = queue[head]!;
    head += 1;
    const d = dist[slot[cur]!]! + 1;
    const [x, y, z] = voxelCoords(cur, dims);
    for (let axis = 0; axis < 6; axis += 1) {
      const ax = x + (axis === 0 ? 1 : axis === 1 ? -1 : 0);
      const ay = y + (axis === 2 ? 1 : axis === 3 ? -1 : 0);
      const az = z + (axis === 4 ? 1 : axis === 5 ? -1 : 0);
      if (ax < 0 || ay < 0 || az < 0 || ax >= nx || ay >= ny || az >= dims[2]) continue;
      const ni = voxelIndex(ax, ay, az, dims);
      const ns = slot[ni]!;
      if (ns < 0 || dist[ns] !== Infinity) continue;
      dist[ns] = d;
      queue[tail] = ni;
      tail += 1;
    }
  }
  return dist;
}

/**
 * Trilinearly sample the per-voxel weight field at a fractional voxel point,
 * accumulating into `out`. Unoccupied corners contribute nothing and are divided
 * out, so a vertex on the surface (whose stencil straddles the boundary) is
 * interpolated from the volume that exists rather than pulled toward zero by the
 * empty side. Returns the total corner coverage — 0 means the vertex is off-volume.
 */
function sampleTrilinear(
  p: VoxelPoint,
  boneCount: number,
  dims: readonly [number, number, number],
  slot: Int32Array,
  field: Float32Array,
  out: Float64Array,
): number {
  const x0 = Math.floor(p.x);
  const y0 = Math.floor(p.y);
  const z0 = Math.floor(p.z);
  const tx = p.x - x0;
  const ty = p.y - y0;
  const tz = p.z - z0;
  out.fill(0);
  let covered = 0;
  for (let c = 0; c < 8; c += 1) {
    const ix = x0 + (c & 1);
    const iy = y0 + ((c >> 1) & 1);
    const iz = z0 + ((c >> 2) & 1);
    if (ix < 0 || iy < 0 || iz < 0 || ix >= dims[0] || iy >= dims[1] || iz >= dims[2]) continue;
    const s = slot[voxelIndex(ix, iy, iz, dims)]!;
    if (s < 0) continue; // unoccupied corner — no contribution, and not counted
    const w = ((c & 1) ? tx : 1 - tx) * (((c >> 1) & 1) ? ty : 1 - ty) * (((c >> 2) & 1) ? tz : 1 - tz);
    if (w <= 0) continue;
    covered += w;
    const base = s * boneCount;
    for (let b = 0; b < boneCount; b += 1) out[b]! += w * field[base + b]!;
  }
  if (covered > 0) for (let b = 0; b < boneCount; b += 1) out[b]! /= covered;
  return covered;
}

/**
 * Compute per-vertex grafted-bone weights over a hand voxel grid. See the module
 * header for the contract; throws {@link BindWeightsError} on a wiring bug.
 */
export function computeBindWeights(
  grid: VoxelGrid,
  segments: readonly BoneSegment[],
  vertices: readonly BindVertex[],
  options: BindWeightsOptions = {},
): BindWeightsResult {
  const issues: string[] = [];
  if (grid.origin === undefined) {
    issues.push('grid has no world embedding (`origin`) — bind needs voxel→world to place vertices');
  }
  if (segments.length === 0) issues.push('no bone segments — nothing to bind to');
  const falloff = options.falloff ?? 3;
  const maxInfluences = options.maxInfluences ?? 4;
  const bleedFloor = options.bleedFloor ?? 1e-3;
  const seedRadius = options.seedRadiusVoxels ?? 1;
  if (!(falloff > 0)) issues.push(`falloff must be > 0, got ${falloff}`);
  if (!Number.isInteger(maxInfluences) || maxInfluences < 1) {
    issues.push(`maxInfluences must be a positive integer, got ${maxInfluences}`);
  }
  if (!(bleedFloor >= 0 && bleedFloor < 1)) issues.push(`bleedFloor must be in [0,1), got ${bleedFloor}`);
  if (!(seedRadius >= 0)) issues.push(`seedRadiusVoxels must be >= 0, got ${seedRadius}`);
  const emitted = segments.filter((s) => s.retain !== true);
  if (segments.length > 0 && emitted.length === 0) {
    issues.push('every segment is `retain` — there is no grafted bone to weight');
  }
  // Duplicate boneIds are a wiring bug for EMITTED segments only (two segments
  // writing influences under one bone would double-count). RETAIN competitors
  // may legitimately FAN — the hand defends its palm with a segment to EACH
  // knuckle (2026-08-20: one segment to the MEAN knuckle was a line defending
  // a surface, and the palm auction went to the digits) — and since retained
  // slots never emit, each fan segment is just one more competitor in the
  // per-voxel normalisation, which is the point.
  const seen = new Set<string>();
  for (const s of emitted) {
    if (seen.has(s.boneId)) issues.push(`duplicate segment for bone "${s.boneId}"`);
    seen.add(s.boneId);
  }
  // A cross-category collision is still a wiring bug (Codex P2, #99): a bone
  // declared retained must never ALSO emit — influences would rewrite an
  // existing rig bone under an id the caller marked hands-off.
  for (const s of segments) {
    if (s.retain === true && seen.has(s.boneId)) {
      issues.push(`bone "${s.boneId}" is both retained and emitted — a retained bone must never be emitted`);
    }
  }
  if (issues.length > 0) throw new BindWeightsError(issues);

  const markers: EvidenceMarker[] = [];
  const dims = grid.dims as readonly [number, number, number];
  const origin = grid.origin!;
  const h = grid.voxelSize;

  // --- compact the occupied set: linear index → dense slot, for per-bone fields ---
  const occupiedList = [...grid.occupied].sort((a, b) => a - b);
  const slot = new Int32Array(dims[0] * dims[1] * dims[2]).fill(-1);
  occupiedList.forEach((v, i) => {
    slot[v] = i;
  });

  // --- seed + run one geodesic field per bone ---
  const boneCount = segments.length;
  const fields: Float32Array[] = [];
  const unseeded: string[] = [];
  const r2 = (seedRadius + 0.5) * (seedRadius + 0.5);
  for (const seg of segments) {
    // A segment seeds every occupied voxel whose CENTRE is within the radius. The
    // centre convention matches voxelPointToWorld, so a joint sitting exactly on a
    // voxel centre seeds that voxel and nothing is off by half a cell.
    const seeds: number[] = [];
    let nearest = -1;
    let nearestD2 = Infinity;
    for (const v of occupiedList) {
      const [x, y, z] = voxelCoords(v, dims);
      const d2 = distSqToSegment({ x, y, z }, seg.from, seg.to);
      if (d2 <= r2) seeds.push(v);
      if (d2 < nearestD2) {
        nearestD2 = d2;
        nearest = v;
      }
    }
    if (seeds.length === 0) {
      // The bone's segment lies outside the volume — a real outcome for a tip
      // extrapolated past the mesh, or a digit placed off a noisy chain. Snap to the
      // single nearest occupied voxel so the bone still drives SOMETHING, and say so.
      if (nearest >= 0) seeds.push(nearest);
      unseeded.push(seg.boneId);
      markers.push({
        code: 'bind-segment-outside-volume',
        detail: { bone: seg.boneId, nearestDistanceVoxels: Number(Math.sqrt(nearestD2).toFixed(3)) },
      });
    }
    fields.push(geodesicField(seeds, dims, slot, occupiedList));
  }
  if (unseeded.length > 0) {
    markers.push({ code: 'bind-segments-outside-volume', detail: { count: unseeded.length } });
  }

  // --- per-voxel normalised weights across ALL competitors (retain included) ---
  const voxelWeights = new Float32Array(occupiedList.length * boneCount);
  let unreachedVoxels = 0;
  for (let s = 0; s < occupiedList.length; s += 1) {
    let total = 0;
    const base = s * boneCount;
    for (let b = 0; b < boneCount; b += 1) {
      const d = fields[b]![s]!;
      if (!Number.isFinite(d)) continue;
      // +1 keeps the seed voxel (d=0) finite and makes the falloff a ratio of
      // "one voxel away" rather than an asymptote at the bone itself.
      const w = Math.pow(1 / (d + 1), falloff);
      voxelWeights[base + b] = w;
      total += w;
    }
    if (total > 0) {
      for (let b = 0; b < boneCount; b += 1) voxelWeights[base + b]! /= total;
    } else {
      unreachedVoxels += 1;
    }
  }
  if (unreachedVoxels > 0) {
    // Occupied volume no bone can reach: a disconnected island left by voxelization.
    markers.push({ code: 'bind-unreachable-voxels', detail: { count: unreachedVoxels } });
  }

  // --- sample every vertex, keep the grafted share ---
  const emitIndex = segments.map((s) => s.retain !== true);
  const influences: VertexInfluence[] = [];
  const scratch = new Float64Array(boneCount);
  let verticesBound = 0;
  let verticesOutsideVolume = 0;
  let maxDigitShare = 0;
  for (const vert of vertices) {
    // Exact inverse of voxelPointToWorld: world = origin + (p + 0.5)·h.
    const p: VoxelPoint = {
      x: (vert.position.x - origin[0]!) / h - 0.5,
      y: (vert.position.y - origin[1]!) / h - 0.5,
      z: (vert.position.z - origin[2]!) / h - 0.5,
    };
    const covered = sampleTrilinear(p, boneCount, dims, slot, voxelWeights, scratch);
    if (covered <= 0) {
      verticesOutsideVolume += 1;
      continue;
    }
    let strongest = 0;
    for (let b = 0; b < boneCount; b += 1) if (scratch[b]! > strongest) strongest = scratch[b]!;
    if (!(strongest > 0)) {
      verticesOutsideVolume += 1;
      continue;
    }
    const floor = strongest * bleedFloor;
    const picked: { boneId: string; weight: number }[] = [];
    for (let b = 0; b < boneCount; b += 1) {
      if (!emitIndex[b]) continue;
      const w = scratch[b]!;
      if (w <= floor) continue;
      picked.push({ boneId: segments[b]!.boneId, weight: w });
    }
    if (picked.length === 0) continue; // wholly retained (palm) — caller leaves it alone
    picked.sort((a, b) => b.weight - a.weight || (a.boneId < b.boneId ? -1 : 1));
    const kept = picked.slice(0, maxInfluences);
    let share = 0;
    for (const k of kept) {
      influences.push({ vertexIndex: vert.index, boneId: k.boneId, weight: k.weight });
      share += k.weight;
    }
    if (share > maxDigitShare) maxDigitShare = share;
    verticesBound += 1;
  }
  if (verticesOutsideVolume > 0) {
    markers.push({ code: 'bind-vertices-outside-volume', detail: { count: verticesOutsideVolume } });
  }

  return { influences, verticesBound, verticesOutsideVolume, maxDigitShare, markers };
}
